import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Breadcrumb, Button, Form, Input, Upload, message, Progress } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { api, uploadDirectToBlob } from '../../utils/api';

const { TextArea } = Input;

const DEFAULT_VERSION = '1.0.0';
const AdminAppCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>('');
  const [installFile, setInstallFile] = useState<File | null>(null);
  const [installFileUrl, setInstallFileUrl] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const [installProgress, setInstallProgress] = useState(0);

  // Generate/revoke preview URL for selected logo
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoPreviewUrl('');
    }
  }, [logoFile]);

  const isValid = useMemo(() => {
    const values = form.getFieldsValue();
    const hasBasics = values?.name && values?.description && values?.appInfo;
    const hasLogo = logoFile || logoUrl;
    const hasInstall = installFile || installFileUrl;
    return !!(hasBasics && hasLogo && hasInstall);
  }, [form, logoFile, logoUrl, installFile, installFileUrl]);

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      // Derive a safe folder prefix from app name
      const slug = String(values.name || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      const basePrefix = slug ? `admin-apps/${slug}` : 'admin-apps/unknown-app';

      // Upload files on submit only
      let finalLogoUrl = logoUrl;
      let finalInstallUrl = installFileUrl;

      if (!finalLogoUrl && logoFile) {
        // upload logo under <prefix>/logo/
        setLogoProgress(0);
        const res = await uploadDirectToBlob(logoFile, `${basePrefix}/logo`, {
          onProgress: (ev) => {
            const pct = Math.round((ev.loadedBytes / (logoFile.size || 1)) * 100);
            setLogoProgress(pct);
          },
          blockSizeMB: 8,
          concurrency: 8,
        });
        finalLogoUrl = res.url;
        setLogoUrl(finalLogoUrl);
      }
      if (!finalInstallUrl && installFile) {
        // upload installer under <prefix>/installer/
        setInstallProgress(0);
        const res = await uploadDirectToBlob(installFile, `${basePrefix}/installer/${DEFAULT_VERSION}`, {
          onProgress: (ev) => {
            const pct = Math.round((ev.loadedBytes / (installFile.size || 1)) * 100);
            setInstallProgress(pct);
          },
          blockSizeMB: 8,
          concurrency: 8,
        });
        finalInstallUrl = res.url;
        setInstallFileUrl(finalInstallUrl);
      }

      // Call backend create API with initial version 1.0.0
      await api.post('/api/admin/apps', {
        name: values.name,
        description: values.description,
        appInfo: values.appInfo,
        logoUrl: finalLogoUrl,
        initialVersion: {
          version: DEFAULT_VERSION,
          changes: 'Initial release',
          installerUrl: finalInstallUrl,
        },
      });
      message.success('アプリを登録しました。');
      navigate('/admin');
    } catch (e) {
      message.error('アプリ登録に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="text-xl font-semibold text-left">アプリの新規登録</h2>
      <Breadcrumb
        items={[
          { title: <Link to="/admin">アプリ一覧</Link> },
          { title: 'アプリの新規登録' },
        ]}
      />

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        className="mt-2"
      >
        {/* 基本情報パネル */}
        <div className="bg-[#F4F6F9] rounded-lg p-4">
          <div className="text-base font-semibold mb-3 text-left">アプリの基本情報</div>
          {/* Row 1: 名前 + ロゴ */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Form.Item
              label={<span>名前 <span className="text-red-500">※</span></span>}
              name="name"
              rules={[{ required: true, message: '' }]}
            >
              <Input placeholder="アプリ名を入力" allowClear />
            </Form.Item>

            <Form.Item
              label={<span>ロゴ <span className="text-red-500">※</span></span>}
              required={false}
            >
              <div className="flex flex-col gap-2 items-start">
                <div className="flex items-center gap-3">
                  <Upload
                    beforeUpload={(file: File) => { setLogoFile(file); return false; }}
                    maxCount={1}
                    showUploadList={logoFile ? { showPreviewIcon: false } : false}
                    accept="image/*"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      className="w-[378px] h-[46px] rounded-[6px] border px-[120px]"
                    >
                      アップロード
                    </Button>
                  </Upload>
                  {logoPreviewUrl && (
                    <img
                      src={logoPreviewUrl}
                      alt="Logo preview"
                      className="h-12 w-12 object-contain rounded border border-gray-200 bg-white"
                    />
                  )}
                  {!logoPreviewUrl && logoUrl && (
                    <a href={logoUrl} target="_blank" rel="noreferrer" className="text-[#0077C8] text-sm">プレビュー</a>
                  )}
                </div>
                {logoProgress > 0 && logoProgress < 100 && (
                  <Progress percent={logoProgress} size="small" className="w-full max-w-[378px]" />
                )}
              </div>
            </Form.Item>
          </div>

          {/* Row 2: 説明 */}
          <Form.Item
            label={<span>説明 <span className="text-red-500">※</span></span>}
            name="description"
            rules={[{ required: true, message: '' }]}
          >
            <Input placeholder="アプリの説明を入力" allowClear />
          </Form.Item>

          {/* Row 3: アプリ情報 */}
          <Form.Item
            label={<span>アプリ情報 <span className="text-red-500">※</span></span>}
            name="appInfo"
            rules={[{ required: true, message: '' }]}
          >
            <TextArea rows={6} placeholder="アプリに関する詳細情報を入力" />
          </Form.Item>
        </div>

        {/* Row 4: インストールファイルのアップロード */}
        <div className="bg-[#F4F6F9] rounded-lg p-4 mt-4">
            <Form.Item label={<span>インストールファイルのアップロード <span className="text-red-500">※</span></span>}>
            <div className="flex flex-col gap-2 items-start">
              <div className="flex items-center gap-3">
                <Upload
                  beforeUpload={(file: File) => { setInstallFile(file); return false; }}
                  maxCount={1}
                  showUploadList={installFile ? { showPreviewIcon: false } : false}
                  accept=".exe,.msi,.zip,.rar,.7z,.dmg,.pkg"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="w-[378px] h-[46px] rounded-[6px] border px-[120px]"
                  >
                    アップロード
                  </Button>
                </Upload>
                {installFile && <span className="text-sm text-gray-600 truncate max-w-[200px]">{installFile.name}</span>}
                {installFileUrl && (
                  <a href={installFileUrl} target="_blank" rel="noreferrer" className="text-[#0077C8] text-sm">ファイルを確認</a>
                )}
              </div>
              {installProgress > 0 && installProgress < 100 && (
                <Progress percent={installProgress} size="small" className="w-full max-w-[378px]" />
              )}
            </div>
            </Form.Item>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button type="primary" htmlType="submit" loading={submitting} disabled={!isValid}>
            {submitting ? '登録中…' : 'アプリ登録'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AdminAppCreatePage;
